/**
 * Assemble the tool map handed to `streamText` — which tools are present, which
 * ones ship their schema eagerly, and where the prompt-cache breakpoint sits.
 *
 * SEAM: everything here changes for ONE reason — the economics of the tool
 * PREFIX (the constant, dominant part of every request). Tool search
 * (`deferLoading`), the hot-tool list and the ephemeral cache breakpoint are a
 * single interlocking decision: Anthropic forbids a tool from having both
 * `defer_loading` and `cache_control`, so the deferral pass and the caching pass
 * must agree on ordering or the cached prefix silently stops hitting. Keeping
 * them in one module keeps that invariant reviewable in one place, separate from
 * the streaming orchestration (which decides WHEN to call these) and from the
 * turn telemetry (which measures the result).
 *
 * Re-exported from `./bedrock-client` so every existing import path still works.
 */
import { tool } from 'ai';
import { getAvailableTools, executeTool } from '../tools/index.js';
import type { ToolExecutionContext } from '../tools/index.js';
import { getToolSchema } from '../tools/schemas/index.js';
import { getToolSearchConfig, isHotTool, type ToolSearchConfig } from '../tools/tool-search/index.js';
import { EPHEMERAL_CACHE } from '../providers/index.js';
import { logger } from '../logger.js';

/**
 * Build the Meridian tool set in AI SDK format (Zod `inputSchema`; the SDK converts to JSON).
 *
 * When `useToolSearch` is true (the agentic chat default), every tool NOT in the hot set
 * (`getToolSearchConfig().nonDeferredTools`) is marked `deferLoading: true` via
 * `providerOptions.anthropic` — its schema is kept OUT of the prompt until Claude finds it
 * through the `toolSearch` server tool (registered by the caller in streamBedrockResponse).
 * Hot tools load eagerly. When false, all tools load eagerly (the non-streaming generate path,
 * which has no tool-search server tool to discover deferred tools with).
 */
export function buildToolsForAI(
  toolContext?: ToolExecutionContext,
  useToolSearch: boolean = false
): Record<string, any> {
  const tools: Record<string, any> = {};
  // Hot = loaded eagerly. Everything else defers, driven by the nonDeferredTools list only
  // (isHotTool), so newly added tools auto-defer without being enumerated anywhere.
  const searchConfig = useToolSearch ? getToolSearchConfig(true) : null;

  for (const toolDef of getAvailableTools()) {
    const zodSchema = getToolSchema(toolDef.id);
    if (!zodSchema) {
      logger.warn('no Zod schema found for tool', { op: 'buildToolsForAI', toolId: toolDef.id });
      continue;
    }

    const defer = !!searchConfig && !isHotTool(toolDef.id, searchConfig);

    tools[toolDef.id] = tool({
      description: toolDef.description,
      inputSchema: zodSchema as any,
      needsApproval: !!toolDef.requiresConfirmation,
      // Deferred tools stay out of context until tool search surfaces them.
      ...(defer ? { providerOptions: { anthropic: { deferLoading: true } } } : {}),
      execute: async (params: Record<string, any>) => {
        try {
          const result = await executeTool(toolDef.id, params, toolContext);

          // Return result in the format expected by the AI SDK
          if (result.type === 'error') {
            return { error: result.error?.message || 'Tool execution failed' };
          }

          return result.data || result;
        } catch (error) {
          logger.error('tool execution error', error, { op: 'buildToolsForAI', toolId: toolDef.id });
          return { error: error instanceof Error ? error.message : 'Unknown error' };
        }
      },
    } as any);
  }

  if (searchConfig) {
    const total = Object.keys(tools).length;
    const hot = searchConfig.nonDeferredTools.length;
    logger.info('meridian tool set built with tool search', {
      op: 'buildToolsForAI',
      totalTools: total,
      hotTools: hot,
      deferred: total - hot,
    });
  }

  return tools;
}

/**
 * Apply tool-search deferral to a CALLER-supplied tool set (e.g. Hub `/chat`,
 * which builds its own ~50-tool registry and passes it via `options.tools`).
 *
 * Without this, caller-supplied tools were sent fully eagerly — every schema in
 * the prompt on every request. Here we keep the small hot set eager and mark
 * every other tool `deferLoading: true`, so its schema stays out of context until
 * Claude surfaces it through the `tool_search` server tool (registered by the
 * caller branch in streamBedrockResponse). The hot list is the single source of
 * truth (isHotTool) — newly added tools defer automatically.
 *
 * Returns a NEW map; the caller's tool objects are shallow-cloned so their
 * existing `providerOptions` are preserved (merged, not overwritten).
 */
export function applyToolSearchDeferral(
  tools: Record<string, unknown>,
  searchConfig: ToolSearchConfig
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  let hot = 0;
  let deferred = 0;

  for (const [name, def] of Object.entries(tools)) {
    if (isHotTool(name, searchConfig)) {
      result[name] = def;
      hot += 1;
      continue;
    }

    const existing = (def && typeof def === 'object' ? (def as Record<string, unknown>) : {}) as {
      providerOptions?: { anthropic?: Record<string, unknown> } & Record<string, unknown>;
    };
    const existingProviderOptions = existing.providerOptions ?? {};
    const existingAnthropic = existingProviderOptions.anthropic ?? {};

    result[name] = {
      ...(def as Record<string, unknown>),
      providerOptions: {
        ...existingProviderOptions,
        anthropic: { ...existingAnthropic, deferLoading: true },
      },
    };
    deferred += 1;
  }

  logger.info('meridian caller tool set built with tool search', {
    op: 'streamBedrockResponse',
    totalTools: hot + deferred,
    hotTools: hot,
    deferred,
  });

  return result;
}

export function isDeferredTool(tool: unknown): boolean {
  return (
    (tool as { providerOptions?: { anthropic?: { deferLoading?: boolean } } })?.providerOptions
      ?.anthropic?.deferLoading === true
  );
}

/**
 * Cache the tool-definition prefix, respecting Anthropic's hard constraint:
 * a tool CANNOT have both `defer_loading: true` and `cache_control`
 * ("Tools with defer_loading cannot use prompt caching"). So only the HOT
 * (eager) tools are cacheable — deferred tools are withheld from context anyway
 * (≈0 tokens), so nothing is lost.
 *
 * Returns a NEW map ordered eager-first, with an ephemeral breakpoint on the
 * LAST eager (function) tool, then the deferred tools (no cache_control) after
 * the breakpoint so no deferred tool sits inside the cached prefix. Callers
 * append the provider-defined `tool_search` AFTER this (its cache_control is
 * ignored by the provider). No-op-returns the input if nothing is cacheable.
 */
export function withCachedToolPrefix(tools: Record<string, unknown>): Record<string, unknown> {
  const eager: Array<[string, unknown]> = [];
  const deferred: Array<[string, unknown]> = [];
  for (const entry of Object.entries(tools)) {
    (isDeferredTool(entry[1]) ? deferred : eager).push(entry);
  }
  if (eager.length === 0) return tools; // all deferred → tools aren't cacheable

  const [lastKey, lastVal] = eager[eager.length - 1];
  const t = (lastVal ?? {}) as { providerOptions?: { anthropic?: Record<string, unknown> } };
  const po = t.providerOptions ?? {};
  eager[eager.length - 1] = [
    lastKey,
    { ...t, providerOptions: { ...po, anthropic: { ...(po.anthropic ?? {}), ...EPHEMERAL_CACHE.anthropic } } },
  ];

  return Object.fromEntries([...eager, ...deferred]);
}
